import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameweekStats } from '../hooks/useFantasyData';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import '../styles/FantasyStats.css';

const FantasyStatsPage = ({ setActiveTab }) => {
  const { t } = useTranslation();
  const [season] = useState(2024);
  const [gameweekNumber, setGameweekNumber] = useState(1);

  const { stats, loading, error } = useGameweekStats(gameweekNumber, season);

  const handlePrevGameweek = () => {
    if (gameweekNumber > 1) {
      setGameweekNumber(gameweekNumber - 1);
    }
  };

  const handleNextGameweek = () => {
    if (gameweekNumber < 38) {
      setGameweekNumber(gameweekNumber + 1);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error} />;
  if (!stats) return <ErrorAlert message={t('fantasy.stats.no_data')} />;

  const completionPercentage = stats.totalFixtures
    ? ((stats.completedFixtures / stats.totalFixtures) * 100).toFixed(1)
    : 0;

  return (
    <div className="fantasy-stats-page container mt-4">
      <button
        className="btn-back-header mb-3"
        onClick={() => setActiveTab(9)}
        aria-label="Back to Fantasy main"
        title="Back to Fantasy"
      >
        ← Back
      </button>
      <h1 className="mb-4">{t('fantasy.stats.title')}</h1>

      <div className="gameweek-navigation mb-4">
        <button
          className="btn btn-primary"
          onClick={handlePrevGameweek}
          disabled={gameweekNumber === 1}
        >
          {t('fantasy.stats.previous')}
        </button>

        <div className="gameweek-info">
          <h2>
            {t('fantasy.stats.gameweek')} {gameweekNumber} / 38
          </h2>
          <p className="season-info">{t('fantasy.stats.season')}: {season}</p>
        </div>

        <button
          className="btn btn-primary"
          onClick={handleNextGameweek}
          disabled={gameweekNumber === 38}
        >
          {t('fantasy.stats.next')}
        </button>
      </div>

      <div className="stats-container">
        <div className="stats-grid">
          <div className="stat-card">
            <h3>{t('fantasy.stats.status')}</h3>
            <p className="stat-value">{stats.status || 'N/A'}</p>
          </div>

          <div className="stat-card">
            <h3>{t('fantasy.stats.total_fixtures')}</h3>
            <p className="stat-value">{stats.totalFixtures || 0}</p>
          </div>

          <div className="stat-card">
            <h3>{t('fantasy.stats.completed_fixtures')}</h3>
            <p className="stat-value">{stats.completedFixtures || 0}</p>
          </div>

          <div className="stat-card">
            <h3>{t('fantasy.stats.completion')}</h3>
            <p className="stat-value">{completionPercentage}%</p>
          </div>
        </div>

        <div className="progress-bar mt-4">
          <div className="progress">
            <div
              className="progress-fill"
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
          <p className="progress-label">
            {stats.completedFixtures} / {stats.totalFixtures} {t('fantasy.stats.fixtures_completed')}
          </p>
        </div>
      </div>

      {stats.topPerformances && stats.topPerformances.length > 0 && (
        <div className="top-performers mt-5">
          <h3>{t('fantasy.stats.top_performers')}</h3>
          <div className="table-responsive">
            <table className="table table-striped">
              <thead className="table-dark">
                <tr>
                  <th>{t('fantasy.stats.player')}</th>
                  <th>{t('fantasy.stats.role')}</th>
                  <th>{t('fantasy.stats.rating')}</th>
                  <th>{t('fantasy.stats.fantasy_points')}</th>
                  <th>{t('fantasy.stats.goals')}</th>
                  <th>{t('fantasy.stats.assists')}</th>
                  <th>{t('fantasy.stats.minutes')}</th>
                </tr>
              </thead>
              <tbody>
                {stats.topPerformances.map((perf, idx) => (
                  <tr key={idx}>
                    <td>{perf.playerName}</td>
                    <td>{perf.role}</td>
                    <td>{perf.rating ? parseFloat(perf.rating).toFixed(2) : 'N/A'}</td>
                    <td className="highlight">{perf.fantasyPoints || 0}</td>
                    <td>{perf.goals || 0}</td>
                    <td>{perf.assists || 0}</td>
                    <td>{perf.playedMinutes || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FantasyStatsPage;
